// src/api/fscApi.js

import { createApiInstance } from './index';
import { withProjectContext } from './projectContext';
import { withFscContext } from './fscCloudContext';

const fscApi = createApiInstance();

const withProjectParams = (params = {}) => withFscContext(withProjectContext(params));
const withProjectBody = (body = {}) => withFscContext(withProjectContext(body));

/**
 * 金管會 FSC API 須帶入 cloud（azure | gcp | aws），可併入 params 或 body。
 * 多專案下拉篩選另帶 cloud_scope（'all' 或單一 subscription / gcp project / aws account）。
 */

// ============ 控制項 API ============

/**
 * 取得控制項列表
 * @param {Object} params - 查詢參數 { cloud, cloud_scope?, level?, parent_uuid?, project_uuid? }
 */
export const getFSCControls = async (params = {}) => {
  try {
    const response = await fscApi.get('fsc/controls/', { params: withProjectParams(params) });
    return response.data;
  } catch (error) {
    console.error('getFSCControls Error:', error);
    throw error;
  }
};

/**
 * 取得控制項樹狀結構
 * @param {Object} params - { cloud, cloud_scope?, project_uuid? }
 */
export const getFSCControlTree = async (params = {}) => {
  try {
    const response = await fscApi.get('fsc/controls/tree/', { params: withProjectParams(params) });
    return response.data;
  } catch (error) {
    console.error('getFSCControlTree Error:', error);
    throw error;
  }
};

/**
 * 取得單一控制項詳情
 * @param {string} controlUuid - 控制項 UUID
 * @param {Object} params - { cloud, cloud_scope?, project_uuid? }
 */
export const getFSCControl = async (controlUuid, params = {}) => {
  try {
    const response = await fscApi.get(`fsc/controls/${controlUuid}/`, {
      params: withProjectParams(params),
    });
    return response.data;
  } catch (error) {
    console.error('getFSCControl Error:', error);
    throw error;
  }
};

/**
 * 新增控制項
 * @param {Object} data - { cloud, cloud_scope, control_id, control_name, parent_uuid?, is_assessable?, recommendation?, remediation_steps?, project_uuid? }
 */
export const createFSCControl = async (data) => {
  try {
    const response = await fscApi.post('fsc/controls/', withProjectBody(data));
    return response.data;
  } catch (error) {
    console.error('createFSCControl Error:', error);
    throw error;
  }
};

/**
 * 更新控制項
 * @param {string} controlUuid - 控制項 UUID
 * @param {Object} data - { cloud, cloud_scope, control_name?, sort_order?, is_assessable?, recommendation?, remediation_steps?, project_uuid? }
 */
export const updateFSCControl = async (controlUuid, data) => {
  try {
    const response = await fscApi.put(`fsc/controls/${controlUuid}/`, withProjectBody(data));
    return response.data;
  } catch (error) {
    console.error('updateFSCControl Error:', error);
    throw error;
  }
};

/**
 * 刪除控制項（軟刪除，含子節點）
 * @param {Object} opts - { control_uuid, project_uuid?, cloud, cloud_scope }
 */
export const deleteFSCControl = async ({ control_uuid, project_uuid, cloud, cloud_scope }) => {
  try {
    const response = await fscApi.delete(`fsc/controls/${control_uuid}/`, {
      params: withProjectParams({ project_uuid, cloud, cloud_scope }),
    });
    return response.data;
  } catch (error) {
    console.error('deleteFSCControl Error:', error);
    throw error;
  }
};

/**
 * 批次新增控制項
 * 後端需要 project_uuid
 * @param {Object} payload - { cloud, cloud_scope, project_uuid: string, controls: Array }
 * controls: [{ parent_control_id?, control_name, recommendation?, remediation_steps? }]
 */
export const bulkCreateFSCControls = async (payload) => {
  try {
    const response = await fscApi.post('fsc/controls/bulk/', withProjectBody(payload));
    return response.data;
  } catch (error) {
    console.error('bulkCreateFSCControls Error:', error);
    throw error;
  }
};

/**
 * 批次更新控制項
 * @param {Object} payload - { cloud, cloud_scope, controls: Array, project_uuid? }
 */
export const bulkUpdateFSCControls = async (payload) => {
  try {
    const response = await fscApi.put('fsc/controls/bulk/', withProjectBody(payload));
    return response.data;
  } catch (error) {
    console.error('bulkUpdateFSCControls Error:', error);
    throw error;
  }
};

/**
 * 批次刪除控制項
 * @param {Object} opts - { cloud, cloud_scope, uuids?, control_ids?, project_uuid? }
 */
export const bulkDeleteFSCControls = async ({ cloud, uuids = [], control_ids: controlIds = [], ...rest } = {}) => {
  try {
    const response = await fscApi.delete('fsc/controls/bulk/', {
      data: withProjectBody({ ...rest, cloud, uuids, control_ids: controlIds }),
    });
    return response.data;
  } catch (error) {
    console.error('bulkDeleteFSCControls Error:', error);
    throw error;
  }
};

// ============ 評估 API ============

/**
 * 取得評估列表
 * @param {Object} params - { cloud, cloud_scope?, control_uuid?, month?, status?, limit?, project_uuid? }
 */
export const getFSCAssessments = async (params = {}) => {
  try {
    const response = await fscApi.get('fsc/assessments/', { params: withProjectParams(params) });
    return response.data;
  } catch (error) {
    console.error('getFSCAssessments Error:', error);
    throw error;
  }
};

/**
 * 取得單一評估詳情
 * @param {string} assessmentUuid - 評估 UUID
 * @param {Object} params - { cloud, cloud_scope?, project_uuid? }
 */
export const getFSCAssessment = async (assessmentUuid, params = {}) => {
  try {
    const response = await fscApi.get(`fsc/assessments/${assessmentUuid}/`, {
      params: withProjectParams(params),
    });
    return response.data;
  } catch (error) {
    console.error('getFSCAssessment Error:', error);
    throw error;
  }
};

/**
 * 新增或更新評估（同月份會更新）
 * @param {Object} data - { cloud, cloud_scope, control_uuid | control_id, assessment_date, status, recommendation?, remediation_steps?, project_uuid? }
 */
export const createFSCAssessment = async (data) => {
  try {
    const response = await fscApi.post('fsc/assessments/', withProjectBody(data));
    return response.data;
  } catch (error) {
    console.error('createFSCAssessment Error:', error);
    throw error;
  }
};

/**
 * 更新評估
 * @param {string} assessmentUuid - 評估 UUID
 * @param {Object} data - { cloud, cloud_scope, status?, recommendation?, remediation_steps?, project_uuid? }
 */
export const updateFSCAssessment = async (assessmentUuid, data) => {
  try {
    const response = await fscApi.put(`fsc/assessments/${assessmentUuid}/`, withProjectBody(data));
    return response.data;
  } catch (error) {
    console.error('updateFSCAssessment Error:', error);
    throw error;
  }
};

/**
 * 刪除評估
 * @param {string} assessmentUuid - 評估 UUID
 * @param {Object} params - { cloud, cloud_scope?, project_uuid? }
 */
export const deleteFSCAssessment = async (assessmentUuid, params = {}) => {
  try {
    const response = await fscApi.delete(`fsc/assessments/${assessmentUuid}/`, {
      params: withProjectParams(params),
    });
    return response.data;
  } catch (error) {
    console.error('deleteFSCAssessment Error:', error);
    throw error;
  }
};

/**
 * 批次新增或更新評估
 * @param {Object} payload - { cloud, cloud_scope, project_uuid?, assessments: Array }
 */
export const bulkCreateFSCAssessments = async (payload) => {
  try {
    const response = await fscApi.post('fsc/assessments/bulk/', withProjectBody(payload));
    return response.data;
  } catch (error) {
    console.error('bulkCreateFSCAssessments Error:', error);
    throw error;
  }
};

/**
 * 批次更新評估
 * @param {Object} payload - { cloud, cloud_scope, project_uuid?, assessments: Array }
 */
export const bulkUpdateFSCAssessments = async (payload) => {
  try {
    const response = await fscApi.put('fsc/assessments/bulk/', withProjectBody(payload));
    return response.data;
  } catch (error) {
    console.error('bulkUpdateFSCAssessments Error:', error);
    throw error;
  }
};

/**
 * 批次刪除評估
 * @param {Object} opts - { cloud, cloud_scope, uuids: Array, project_uuid? }
 */
export const bulkDeleteFSCAssessments = async ({ cloud, uuids = [], ...rest } = {}) => {
  try {
    const response = await fscApi.delete('fsc/assessments/bulk/', {
      data: withProjectBody({ ...rest, cloud, uuids }),
    });
    return response.data;
  } catch (error) {
    console.error('bulkDeleteFSCAssessments Error:', error);
    throw error;
  }
};
